import { useEffect, useRef, useState } from "react";
import Screen from "../../components/Screen";
import { PrimaryBtn } from "../../components/Buttons";
import Notice from "../../components/Notice";
import { infoIcon } from "../../components/Ico";
import LocateSheet from "../../components/LocateSheet";
import { resolveLocateGate } from "@components/address/func/locatePermission";
import MapPicker from "@components/address/MapPicker";
import { getCurrentLocation, geocodeToAddress, addressToGeocode, combinedAddress } from "@components/address/func/addressTool";

interface NpAddressProps {
  /** match=用地址找所屬瓦斯行；plain=新客戶只登記地址 */
  mode: "match" | "plain";
  /** 瓦斯行已知（傳單·有指定）→ 地址純為配送用，不再說「尋找瓦斯行」 */
  supplierKnown?: boolean;
  /** 已於前面（輸入手機那關）允許定位 → 直接帶入地址/門牌，且不再彈定位詢問 */
  prefillAddr?: string;
  prefillDoor?: string;
  /** 前面允許定位取得的 GPS 座標 → 直接定位地圖 pin（取代寫死座標） */
  prefillLat?: number;
  prefillLng?: number;
  skipLocate?: boolean;
  /** 送出後端比對/推薦進行中：按鈕轉圈 + 禁點，避免看起來卡住。 */
  busy?: boolean;
  /** 覆蓋預設標題/副標——用於既有客戶「補齊配送地址」等非新客戶語意的情境，不要複製整頁改文案。 */
  title?: string;
  subtitle?: string;
  /**
   * 送出地址。第 2、3 參數是「地圖上實際的配送定位座標」——
   * 客戶在地圖確認/微調過的位置比 geocode 猜的更可信，且台灣詳細地址（含門牌樓層）
   * 用 OpenStreetMap 常查不到座標；不帶出去的話，下游推薦瓦斯行會因為算不出距離
   * 而放棄配對、把客戶誤丟進「等待安排瓦斯行」名單。
   */
  onDone: (address: string, lat?: number, lng?: number, note?: string) => void;
  /** 省略＝這一頁沒有上一步（流程第一頁）→ 不顯示返回鍵。由 Onboarding 的導航堆疊 canGoBack 決定。 */
  onBack?: () => void;
}

const fieldCls =
  "w-full h-[54px] px-4 border-none rounded-[14px] bg-[#F1F5F9] text-base outline-none box-border text-[#333]";

/** 傳單・無指定瓦斯行：填寫地址（地址 + 門牌/樓層 + 地圖微調，地圖不影響輸入的地址）。 */
const NpAddress = ({ mode, supplierKnown, prefillAddr, prefillDoor, prefillLat, prefillLng, skipLocate, busy, title, subtitle, onDone, onBack }: NpAddressProps) => {
  const [addr, setAddr] = useState(prefillAddr || "");
  const [door, setDoor] = useState(prefillDoor || "");
  /** MapPicker 定位中 → 卡控送出，避免用到尚未更新的座標 */
  const [isMapGeocoding, setIsMapGeocoding] = useState(false);
  const [floor, setFloor] = useState("");
  // 給配送員的指示：隨 onDone 送出 → entryAddressNote → walk-in payload → 兩張地址表的 extra
  const [note, setNote] = useState("");
  // 說明卡預設不開：上一關沒帶到位置時，由下面的 gate effect 決定要直接叫系統窗還是彈卡片
  const [ask, setAsk] = useState(false);
  const [blocked, setBlocked] = useState(false); // 重試仍拿不到權限 → 卡片換成「怎麼救」
  const [locating, setLocating] = useState(false);
  // 地圖（react-leaflet）mount 很重，同步掛載會擋住換頁 render（進地址頁「卡一下」）。
  // 延到下一個 frame 再掛，讓地址表單先畫出來、地圖用佔位補上 → 換頁即時、不卡。
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setMapReady(true));
    });
    return () => { cancelAnimationFrame(outer); cancelAnimationFrame(inner); };
  }, []);

  const [mapForm, setMapForm] = useState<any>({
    latitude: prefillLat ?? 24.1477,
    longitude: prefillLng ?? 120.6736,
    city: "",
    area: "",
    road: "",
    streetNumber: "",
    floor: "",
    room: "",
  });
  // 使用者是否手動拖曳過地圖：拖過之後就不再用地址 geocode 覆蓋 pin（尊重手動微調），
  // 但只要地址欄再次被編輯就重置 → 「改地址」的意圖優先於上一次的微調。
  const draggedRef = useRef(false);
  const mapSetValue = ((name: string, value: any) => {
    if (name === "latitude" || name === "longitude") draggedRef.current = true;
    setMapForm((f: any) => ({ ...f, [name]: value }));
  }) as any;

  /** 使用者已動過地址/門牌欄，或本頁自己跑過定位 → 晚到的 prefill 不得覆蓋 */
  const touchedRef = useRef(false);

  /** 取定位並帶入地址；回傳有沒有拿到位置（卡片模式要靠這個決定關掉還是換 blocked 畫面）。 */
  const runLocate = async (): Promise<boolean> => {
    touchedRef.current = true;
    setLocating(true);
    try {
      const loc = await getCurrentLocation();
      if (loc.state !== "success") return false; // 拒絕/失敗 → 手動填
      const lat = Number(loc.lat);
      const lng = Number(loc.lng);
      setMapForm((f: any) => ({ ...f, latitude: lat, longitude: lng }));
      // GPS 實測座標比「地址 geocode」精確 → 標記為已定位，別讓下面的 debounce 用反查地址把 pin 蓋掉
      draggedRef.current = true;
      const rev = await geocodeToAddress(lat, lng);
      if (rev && (rev.city || rev.road)) {
        setAddr(combinedAddress({ city: rev.city, area: rev.area, road: rev.road }));
        setDoor(rev.streetNumber ? (rev.streetNumber.endsWith("號") ? rev.streetNumber : `${rev.streetNumber}號`) : "");
      }
      return true;
    } finally {
      setLocating(false);
    }
  };

  /**
   * 上一關沒帶到位置 → 本頁自己取。跟上一關同一套規則：預設直接叫系統窗（點一次就好），
   * 只有已知拿不到（先前被拒 / 權限 denied）才彈說明卡 —— 那時系統窗不會跳，不會問兩遍。
   * 直接叫系統窗這條失敗就安靜收掉：使用者剛拒絕完，再彈卡片等於同一件事追問兩次。
   */
  // 同 Onboarding 的 gate：用 ref 擋重入，不靠 cleanup 的 cancelled 旗標 —— 那會在 effect
  // 重跑時把「還在 await resolveLocateGate」的那次丟掉，定位就默默不執行了。
  const locateStartedRef = useRef(false);
  useEffect(() => {
    if (skipLocate || locateStartedRef.current) return;
    locateStartedRef.current = true;
    void (async () => {
      const gate = await resolveLocateGate();
      if (gate === "ask") setAsk(true);
      else void runLocate();
    })();
    // runLocate 每次 render 重建，放進 deps 會讓定位重跑；ref 已擋住重入
    // eslint-disable-next-line
  }, [skipLocate]);

  /**
   * 晚到的 prefill 同步。
   *
   * 上一關（掃鋼瓶／填手機）按「允許定位」後，定位是丟到背景跑的，不擋畫面。
   * 結果通常在中間那關 OTP 就到齊，本頁一 mount 就有 prefill；但萬一使用者跑得比 GPS 快
   * （或反查慢），mount 時 prefill 還是空的 —— 而下面幾個 state 都是 useState(prefill…) 只讀一次，
   * 沒有這段同步的話：地址不會補進來，而且本頁會再彈一次定位詢問（同一件事問兩遍）。
   *
   * 只處理「晚到」：first render 直接跳過，維持既有 mount 行為不變。
   */
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    if (!skipLocate || touchedRef.current || locating) return;
    touchedRef.current = true;
    if (prefillAddr) setAddr(prefillAddr);
    if (prefillDoor) setDoor(prefillDoor);
    if (typeof prefillLat === "number" && typeof prefillLng === "number") {
      setMapForm((f: any) => ({ ...f, latitude: prefillLat, longitude: prefillLng }));
      // GPS 實測座標比地址 geocode 精確 → 擋掉下面的 debounce 用反查地址把 pin 蓋掉
      draggedRef.current = true;
    }
    setAsk(false); // 本頁的定位詢問還開著的話一併收掉，避免問第二次
  }, [skipLocate, prefillAddr, prefillDoor, prefillLat, prefillLng, locating]);

  const valid = addr.trim().length > 0;
  // 樓層併入送出的地址字串：後端地址精確比對是「整串正規化相等」，樓層納入後
  // 「同門牌不同樓層 / stored 無樓層」就不算 exact → 進 AW 相似度比對（避免公寓大樓誤判同一戶）。
  // 純數字樓層補「樓」；B1／地下等特殊格式原樣帶。
  /**
   * 門牌／樓層自動補字尾，沿用既有 combinedAddress（addressTool）的慣例：
   * 「使用者自己寫了就不重複補，沒寫才補」（James 2026-07-20 拍板）。
   * 原本這裡手寫了一套只補「樓」、不補「號」的邏輯，導致手動輸入的地址是「港墘路335」，
   * 而瓦斯行後台建檔慣用「港墘路335號」——差一個字就讓地址精確比對整條失效
   * （後端雖已在比對時忽略字尾「號」，但存進 DB 的仍是原字串，AW 顯示與後續比對都以補齊為佳）。
   * 只在「結尾是數字」時補，避免把「…巷口」「…之1」等非門牌結尾誤加單位。
   */
  const withSuffix = (v: string, suffix: string) => {
    const t = v.trim();
    if (!t || t.endsWith(suffix)) return t;
    return /\d$/.test(t) ? `${t}${suffix}` : t;
  };
  const floorPart = withSuffix(floor, "樓");
  // 門牌欄有填 → 補在門牌上；門牌留空（整串打在地址欄）→ 補在地址結尾
  const doorPart = door.trim() ? withSuffix(door, "號") : "";
  const addrPart = door.trim() ? addr.trim() : withSuffix(addr, "號");
  const full = `${addrPart}${doorPart}${floorPart}`.trim();

  /**
   * 地址改變 → 地圖 pin 跟著移動（debounce 700ms）。
   *
   * 原本 pin 只吃 prefillLat/prefillLng（前一關的 GPS 定位）：使用者把地址改成別的縣市時 pin 還停在原地，
   * 畫面上「拖曳地圖微調配送位置」的語意也不成立（微調的前提是 pin 一開始就在那個地址上）。
   * 實測遇過「地址寫台中、pin 停在台北」→ 自動配對到 137 公里外的瓦斯行。
   *
   * 注意：這裡的 geocode 只影響「畫面上的 pin」。送出後端後，後端會用地址自己重算權威座標
   * （onboarding-geocode.service），所以就算這裡查不到、或 OSM 對台灣門牌精度不足，比對結果仍正確。
   */
  const lastGeocodedRef = useRef<string | null>(null);
  useEffect(() => {
    const target = full.trim();
    // 地址太短（還在打字）／已對這串查過／使用者剛手動拖過 → 不動 pin
    if (target.length < 6 || target === lastGeocodedRef.current || draggedRef.current) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const res = await addressToGeocode(target);
      if (cancelled || res.state !== "success") return;
      const lat = Number(res.lat);
      const lng = Number(res.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      lastGeocodedRef.current = target;
      // 直接 setMapForm（不走 mapSetValue）→ 不會被誤記成「使用者手動拖曳」
      setMapForm((f: any) => ({ ...f, latitude: lat, longitude: lng }));
    }, 700);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [full]);

  return (
    <Screen
      title={title ?? (supplierKnown ? "填寫配送地址" : "填寫地址")}
      subtitle={subtitle ?? (supplierKnown ? "填寫瓦斯配送的地址" : mode === "match" ? "用地址為您尋找所屬瓦斯行" : "填寫配送地址，完成加入")}
      onBack={onBack}
      collapsibleHeader
      footer={
        <PrimaryBtn
          active={valid && !isMapGeocoding}
          busy={busy || isMapGeocoding}
          busyText={isMapGeocoding ? "定位中…" : !supplierKnown && mode === "match" ? "尋找所屬瓦斯行…" : "處理中…"}
          onClick={() => onDone(full, mapForm.latitude, mapForm.longitude, note.trim() || undefined)}
        >
          {!supplierKnown && mode === "match" ? "查詢" : "完成"}
        </PrimaryBtn>
      }
    >
      <div className="text-[13px] text-[#475569] font-semibold mb-2">地址</div>
      <div className="flex items-center gap-2.5 h-[54px] px-[14px] rounded-[14px] bg-[#F1F5F9] mb-4">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0">
          <path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" stroke="#2196f3" strokeWidth="1.8" />
          <circle cx="12" cy="10" r="2.5" stroke="#2196f3" strokeWidth="1.8" />
        </svg>
        <input
          value={addr}
          onChange={(e) => { draggedRef.current = false; touchedRef.current = true; setAddr(e.target.value); }}
          placeholder="搜尋或輸入配送地址"
          className="flex-1 border-none outline-none text-[15px] text-[#333] bg-transparent"
        />
      </div>

      <div className="flex gap-2.5 mb-4">
        <div className="flex-1">
          <div className="text-[13px] text-[#475569] font-semibold mb-2">門牌號</div>
          <input value={door} onChange={(e) => { draggedRef.current = false; touchedRef.current = true; setDoor(e.target.value); }} placeholder="例：20 號" className={fieldCls} />
        </div>
        <div className="flex-1">
          <div className="text-[13px] text-[#475569] font-semibold mb-2">樓層（選填）</div>
          <input value={floor} onChange={(e) => setFloor(e.target.value)} placeholder="例：5 樓 / B1" className={fieldCls} />
        </div>
      </div>

      <div className="text-[13px] text-[#475569] font-semibold mb-2">給配送員的指示（選填）</div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="例：放門口、按電鈴、有大型犬、樓梯較窄、鐵捲門白天開…"
        rows={2}
        className="w-full px-4 py-3 border-none rounded-[14px] bg-[#F1F5F9] text-[15px] leading-[1.5] outline-none box-border text-[#333] resize-none mb-4"
      />

      <div className="text-[13px] text-[#475569] font-semibold mb-2">配送位置微調</div>
      <div className="rounded-[14px] overflow-hidden border border-[#E7ECF3] mb-2">
        {mapReady ? (
          <MapPicker formFields={mapForm} setValue={mapSetValue} moveHintText="拖曳地圖微調配送位置" onGeocodingChange={setIsMapGeocoding} addressText={addr} />
        ) : (
          <div style={{ height: 250 }} className="flex flex-col items-center justify-center gap-2 bg-[#F1F5F9] text-[#94A3B8]">
            <span className="inline-block w-5 h-5 border-2 border-[#CBD5E1] border-t-transparent rounded-full animate-spin" />
            <span className="text-[12px]">地圖載入中…</span>
          </div>
        )}
      </div>
      <div className="mb-1">
        <Notice tone="blue" icon={infoIcon}>
          {!supplierKnown && mode === "match" ? "用您的地址比對所屬瓦斯行；" : ""}拖曳地圖可微調配送定位，<b>不會改變上方輸入的地址</b>。
        </Notice>
      </div>

      <LocateSheet
        open={ask}
        locating={locating}
        blocked={blocked}
        onAllow={() => {
          void runLocate().then((ok) => (ok ? setAsk(false) : setBlocked(true)));
        }}
        onManual={() => setAsk(false)}
      />
    </Screen>
  );
};

export default NpAddress;
